import { useState, useEffect } from 'react';
import { getImageUrl, trackHit } from '../utils/api';
import { ICONS } from '../utils/icons';

// Backend URL - Production'da relative path kullan
const isProduction = import.meta.env.PROD || (typeof window !== 'undefined' && window.location.hostname !== 'localhost');
const BACKEND_URL = import.meta.env.VITE_BACKEND_BASE_URL || (isProduction ? '' : 'http://localhost:3000');

// Message Modal Component - Mobile App Style Chat UI
const MessageModal = ({ isOpen, onClose, restaurant, organizationId, settings = {} }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        message: ''
    });
    const [honeypot, setHoneypot] = useState(''); // Bot trap
    const [captchaAnswer, setCaptchaAnswer] = useState('');
    const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    // Generate new captcha on mount
    useEffect(() => {
        if (isOpen) {
            generateCaptcha();
            setSent(false);
            setError('');
            setFormData({ name: '', phone: '', message: '' });
            setCaptchaAnswer('');
        }
    }, [isOpen]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const generateCaptcha = () => {
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setCaptcha({ num1, num2 });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Bot check - honeypot should be empty
        if (honeypot) {
            console.log('Bot detected');
            return;
        }

        // Captcha validation
        if (parseInt(captchaAnswer) !== captcha.num1 + captcha.num2) {
            setError('Doğrulama yanlış. Lütfen tekrar deneyin.');
            generateCaptcha();
            setCaptchaAnswer('');
            return;
        }

        // Validation
        if (!formData.name.trim() || !formData.phone.trim() || !formData.message.trim()) {
            setError('Lütfen tüm alanları doldurun.');
            return;
        }

        // Phone validation (Turkish format)
        const phoneRegex = /^[0-9]{10,11}$/;
        const cleanPhone = formData.phone.replace(/[^0-9]/g, '');
        if (!phoneRegex.test(cleanPhone)) {
            setError('Geçerli bir telefon numarası girin.');
            return;
        }

        setSending(true);

        try {
            const response = await fetch(`${BACKEND_URL}/api/organizations/${organizationId}/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name.trim(),
                    phone: cleanPhone,
                    message: formData.message.trim(),
                    subject: 'Menü Mesajı'
                })
            });

            const data = await response.json();

            if (data.success || response.ok) {
                setSent(true);
            } else {
                setError(data.message || 'Mesaj gönderilemedi. Lütfen tekrar deneyin.');
            }
        } catch (err) {
            console.error('Send message error:', err);
            setError('Bağlantı hatası. Lütfen tekrar deneyin.');
        } finally {
            setSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal - Mobile App Style */}
            <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up overflow-hidden">
                {/* Header - Chat Style */}
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-white/30">
                        {getImageUrl(settings?.restaurant_logo || restaurant?.logo) ? (
                            <img 
                                src={getImageUrl(settings?.restaurant_logo || restaurant?.logo)} 
                                alt={restaurant?.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <span className="text-orange-500 text-lg font-bold">
                                {restaurant?.name?.charAt(0) || 'R'}
                            </span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg truncate">{restaurant?.name || 'Restoran'}</h3>
                        <p className="text-xs text-white/80 line-clamp-1">{restaurant?.description || 'Geri bildiriminizi bekliyoruz'}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5">
                    {sent ? (
                        /* Success State */
                        <div className="text-center py-10">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                                <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">Mesajınız Gönderildi!</h4>
                            <p className="text-gray-500 mb-6">
                                En iyi hizmeti vermek için çok çalışıyoruz. Geri bildiriminiz için teşekkür ederiz.
                            </p>
                            <button
                                onClick={onClose}
                                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-lg transition"
                            >
                                Tamam
                            </button>
                        </div>
                    ) : (
                        /* Form */
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Welcome Message Bubble */}
                            <div className="flex gap-3 mb-6">
                                <div className="w-10 h-10 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                    {getImageUrl(settings?.restaurant_logo || restaurant?.logo) ? (
                                        <img 
                                            src={getImageUrl(settings?.restaurant_logo || restaurant?.logo)} 
                                            alt={restaurant.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-orange-500 text-sm font-bold">
                                            {restaurant?.name?.charAt(0) || 'R'}
                                        </span>
                                    )}
                                </div>
                                <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                                    <p className="text-sm text-gray-700">
                                        Merhaba! 👋 Geri bildiriminiz bizim için çok değerli. Öneri, şikayet veya memnuniyetinizi bizimle paylaşın.
                                    </p>
                                </div>
                            </div>

                            {/* Honeypot - Hidden from users, bots will fill it */}
                            <input
                                type="text"
                                name="website"
                                value={honeypot}
                                onChange={(e) => setHoneypot(e.target.value)}
                                style={{ display: 'none' }}
                                tabIndex={-1}
                                autoComplete="off"
                            />

                            {/* Name Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Ad Soyad
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Adınız ve soyadınız"
                                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-0 outline-none transition text-gray-900 placeholder-gray-400"
                                    required
                                />
                            </div>

                            {/* Phone Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Telefon
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    placeholder="05XX XXX XX XX"
                                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-0 outline-none transition text-gray-900 placeholder-gray-400"
                                    required
                                />
                            </div>

                            {/* Message Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Mesajınız
                                </label>
                                <textarea
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    placeholder="Mesajınızı yazın..."
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-0 outline-none transition text-gray-900 placeholder-gray-400 resize-none"
                                    required
                                />
                            </div>

                            {/* Captcha */}
                            <div className="bg-gray-50 rounded-xl p-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    🤖 Robot olmadığınızı doğrulayın
                                </label>
                                <div className="flex items-center gap-3">
                                    <div className="bg-white px-4 py-2 rounded-lg border-2 border-gray-200 font-mono text-lg font-bold text-gray-700">
                                        {captcha.num1} + {captcha.num2} = ?
                                    </div>
                                    <input
                                        type="number"
                                        value={captchaAnswer}
                                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                                        placeholder="Cevap"
                                        className="w-24 px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-orange-500 outline-none transition text-center font-mono text-lg"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={generateCaptcha}
                                        className="p-2 text-gray-400 hover:text-gray-600 transition"
                                        title="Yenile"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {error}
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={sending}
                                className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-orange-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {sending ? (
                                    <>
                                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Gönderiliyor...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        Mesaj Gönder
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* Animation Style */}
            <style>{`
                @keyframes slide-up {
                    from {
                        transform: translateY(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }
                .animate-slide-up {
                    animation: slide-up 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

const MenuFooter = ({ restaurant, organizationId, categories = [], onCategoryClick, onHomeClick, settings = {}, isSettingEnabled = () => true, navbarItems: propNavbarItems = [], themeStyles = {} }) => {
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showNoCampaignModal, setShowNoCampaignModal] = useState(false);

    // Default navbar items if none provided
    const defaultNavbarItems = [
        { id: 'home', label: 'Ana Sayfa', icon: 'home', type: 'internal', target: 'home', enabled: true },
        { id: 'categories', label: 'Kategoriler', icon: 'grid', type: 'internal', target: 'categories', enabled: true },
        { id: 'campaigns', label: 'Kampanyalar', icon: 'gift', type: 'internal', target: 'campaigns', enabled: true },
        { id: 'cocktails', label: 'İmza Kokteyller', icon: 'cocktail', type: 'category', target: '', enabled: true },
        { id: 'contact', label: 'Bize Yazın', icon: 'message', type: 'internal', target: 'contact', enabled: true }
    ];
    
    // Use provided navbar items or fallback to defaults
    const navbarItems = propNavbarItems && propNavbarItems.length > 0 ? propNavbarItems : defaultNavbarItems;

    // WhatsApp link with preset message
    const whatsappNumber = restaurant?.whatsapp_number?.replace(/[^0-9]/g, '') || restaurant?.phone?.replace(/[^0-9]/g, '');
    const whatsappMessage = encodeURIComponent(`Merhaba, bilgi almak istiyorum!\n\n${typeof window !== 'undefined' ? window.location.href : ''}`);
    const whatsappUrl = whatsappNumber ? `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${whatsappMessage}` : null;

    // Find category by id or name
    const findCategoryById = (id) => {
        return categories.find(cat => cat.id === parseInt(id) || cat.id === id);
    };

    const findCategoryByName = (names) => {
        const normalizedNames = names.map(n => n.toLowerCase().trim());
        return categories.find(cat => 
            normalizedNames.includes(cat.name?.toLowerCase().trim()) ||
            normalizedNames.includes(cat.slug?.toLowerCase().trim())
        );
    };

    const kampanyalarCategory = findCategoryByName(['kampanyalar', 'kampanya', 'campaigns']);
    const hasActiveCampaigns = kampanyalarCategory && kampanyalarCategory.products && kampanyalarCategory.products.length > 0;

    // Icon renderer - uses centralized ICONS library
    const renderIcon = (iconName) => {
        const icon = ICONS[iconName];
        const fallback = ICONS.home;
        const iconData = icon || fallback;
        
        return (
            <svg 
                className="w-6 h-6" 
                fill={iconData.fill ? 'currentColor' : 'none'} 
                stroke="currentColor" 
                viewBox="0 0 24 24"
            >
                <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d={iconData.path} 
                />
            </svg>
        );
    };

    // Handle navbar item click
    const handleNavbarItemClick = (item) => {
        console.log('🔘 Navbar item clicked:', item);
        
        // Determine action based on type OR id (fallback for items without type)
        const itemType = item.type || (item.id === 'home' ? 'internal' : 
                                        item.id === 'categories' ? 'internal' :
                                        item.id === 'campaigns' ? 'internal' :
                                        item.id === 'contact' ? 'internal' :
                                        item.id === 'cocktails' ? 'category' : 'internal');
        
        const itemTarget = item.target || item.id; // Use id as target if target is missing
        
        console.log('🎯 Resolved type:', itemType, 'target:', itemTarget);
        
        switch (itemType) {
            case 'internal':
                if (itemTarget === 'home') {
                    if (onHomeClick) onHomeClick();
                    else window.scrollTo({ top: 0, behavior: 'smooth' });
                } else if (itemTarget === 'categories') {
                    if (onHomeClick) onHomeClick();
                    setTimeout(() => {
                        document.getElementById('categories-section')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                } else if (itemTarget === 'campaigns') {
                    if (hasActiveCampaigns && onCategoryClick) {
                        onCategoryClick(kampanyalarCategory);
                    } else {
                        setShowNoCampaignModal(true);
                    }
                } else if (itemTarget === 'contact') {
                    // Track contact/message click
                    if (organizationId) {
                        trackHit(organizationId, 'contact', 0).catch(err => console.debug('Track contact click error:', err));
                    }
                    if (isSettingEnabled('enable_contact_form', true)) {
                        setShowMessageModal(true);
                    }
                } else if (itemTarget === 'footer') {
                    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }
                break;
            case 'category':
                // Try to find category by ID first
                let category = findCategoryById(itemTarget);
                
                // If not found and this looks like İmza Kokteyller, search by name
                if (!category && (item.id === 'cocktails' || item.label?.toLowerCase().includes('kokteyl'))) {
                    category = findCategoryByName(['imza kokteyller', 'İmza Kokteyller', 'imza kokteylleri', 'signature cocktails', 'imza-kokteyller']);
                }
                
                if (category && onCategoryClick) {
                    console.log('✅ Opening category:', category.name);
                    onCategoryClick(category);
                } else {
                    console.log('❌ Category not found for:', item);
                }
                break;
            case 'whatsapp':
                // Track WhatsApp click
                if (organizationId) {
                    trackHit(organizationId, 'whatsapp', 0).catch(err => console.debug('Track WhatsApp click error:', err));
                }
                if (item.url) {
                    const waNum = item.url.replace(/[^0-9]/g, '');
                    window.open(`https://api.whatsapp.com/send?phone=${waNum}&text=${whatsappMessage}`, '_blank');
                } else if (whatsappUrl) {
                    window.open(whatsappUrl, '_blank');
                }
                break;
            case 'phone':
                // Track phone click
                if (organizationId) {
                    trackHit(organizationId, 'phone', 0).catch(err => console.debug('Track phone click error:', err));
                }
                if (item.url) {
                    window.location.href = `tel:${item.url}`;
                }
                break;
            case 'external':
                if (item.url) {
                    window.open(item.url, '_blank');
                }
                break;
            default:
                console.log('⚠️ Unknown item type:', itemType);
        }
    };

    // Get navbar background color from theme
    const navbarBgColor = themeStyles?.['--navbar-bg-color'] || '#ffffff';
    const navbarActiveColor = themeStyles?.['--navbar-active-color'] || '#f97316';
    const primaryColor = themeStyles?.['--primary-color'] || '#f97316';

    return (
        <>
            {/* Message Modal */}
            {isSettingEnabled('enable_contact_form', true) && (
                <MessageModal 
                    isOpen={showMessageModal} 
                    onClose={() => setShowMessageModal(false)}
                    restaurant={restaurant}
                    organizationId={organizationId}
                    settings={settings}
                />
            )}

            {/* No Campaign Modal */}
            {showNoCampaignModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setShowNoCampaignModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-scale-up">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                            <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Aktif Kampanya Bulunmuyor</h3>
                        <p className="text-gray-500 text-sm mb-6">
                            Şu anda aktif bir kampanya bulunmuyor. Lütfen daha sonra tekrar deneyiniz!
                        </p>
                        <button
                            onClick={() => setShowNoCampaignModal(false)}
                            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-lg transition"
                        >
                            Tamam
                        </button>
                    </div>
                    <style>{`
                        @keyframes scale-up {
                            from {
                                transform: scale(0.9);
                                opacity: 0;
                            }
                            to {
                                transform: scale(1);
                                opacity: 1;
                            }
                        }
                        .animate-scale-up {
                            animation: scale-up 0.2s ease-out;
                        }
                    `}</style>
                </div>
            )}

            {/* Footer Navbar - Mobile Bottom Navigation - Dynamic */}
            <nav 
                className="fixed bottom-0 left-0 right-0 backdrop-blur-md border-t border-gray-200 z-40 md:hidden safe-area-inset-bottom"
                style={{ backgroundColor: `${navbarBgColor}f2` }}
            >
                <div className="flex items-center justify-around h-16 max-w-lg mx-auto">
                    {navbarItems.slice(0, 5).map((item, index) => {
                        // Determine if this is the center (campaign) item
                        const isCenterItem = index === Math.floor(Math.min(navbarItems.length, 5) / 2);
                        const isCampaignType = item.target === 'campaigns' || item.id === 'campaigns';
                        
                        if (isCenterItem && isCampaignType) {
                            return (
                                <button
                                    key={item.id || index}
                                    onClick={() => handleNavbarItemClick(item)}
                                    className="flex flex-col items-center justify-center -mt-6"
                                >
                                    <div 
                                        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                                        style={{ 
                                            background: `linear-gradient(to right, ${primaryColor}, ${navbarActiveColor})`,
                                            boxShadow: `0 10px 15px -3px ${primaryColor}40`
                                        }}
                                    >
                                        <span className="text-white">{renderIcon(item.icon)}</span>
                                    </div>
                                    <span 
                                        className="text-[10px] font-medium mt-1"
                                        style={{ color: navbarActiveColor }}
                                    >
                                        {item.label}
                                    </span>
                                </button>
                            );
                        }
                        
                        return (
                            <button
                                key={item.id || index}
                                onClick={() => handleNavbarItemClick(item)}
                                className="flex flex-col items-center justify-center space-y-0.5 text-gray-500 transition-colors py-2 px-3"
                                style={{ '--hover-color': navbarActiveColor }}
                            >
                                {renderIcon(item.icon)}
                                <span className="text-[10px] font-medium">{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>

            {/* Footer - Desktop */}
            <footer id="contact-section" className="bg-gradient-to-b from-gray-900 to-gray-950 text-white mt-16 pb-24 md:pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {/* Restaurant Info */}
                        <div>
                            <h3 className="text-xl font-bold mb-4 text-white">{restaurant?.name}</h3>
                            {restaurant?.description && (
                                <p className="text-gray-400 text-sm mb-6 leading-relaxed">{restaurant.description}</p>
                            )}
                            {restaurant?.address && (
                                <div className="flex items-start space-x-3 text-gray-400 text-sm">
                                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <span className="leading-relaxed">{restaurant.address}</span>
                                </div>
                            )}
                        </div>

                        {/* Contact */}
                        <div>
                            <h3 className="text-xl font-bold mb-4 text-white">İletişim</h3>
                            <div className="space-y-4">
                                {restaurant?.phone && (
                                    <a 
                                        href={`tel:${restaurant.phone}`} 
                                        onClick={() => organizationId && trackHit(organizationId, 'phone', 0).catch(err => console.debug('Track phone click error:', err))}
                                        className="flex items-center space-x-3 text-gray-400 hover:text-orange-400 transition group"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-white/10 group-hover:bg-orange-500/20 flex items-center justify-center transition">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm">{restaurant.phone}</span>
                                    </a>
                                )}
                                {whatsappUrl && isSettingEnabled('enable_whatsapp', true) && (
                                    <a 
                                        href={whatsappUrl} 
                                        onClick={() => organizationId && trackHit(organizationId, 'whatsapp', 0).catch(err => console.debug('Track WhatsApp click error:', err))}
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="flex items-center space-x-3 text-gray-400 hover:text-green-400 transition group"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-white/10 group-hover:bg-green-500/20 flex items-center justify-center transition">
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                            </svg>
                                        </div>
                                        <span className="text-sm">WhatsApp ile Yazın</span>
                                    </a>
                                )}
                                {/* Bize Yazın Button - Desktop */}
                                {isSettingEnabled('enable_contact_form', true) && (
                                    <button 
                                        onClick={() => {
                                            // Track contact/message click
                                            if (organizationId) {
                                                trackHit(organizationId, 'contact', 0).catch(err => console.debug('Track contact click error:', err));
                                            }
                                            setShowMessageModal(true);
                                        }}
                                        className="flex items-center space-x-3 text-gray-400 hover:text-orange-400 transition group w-full text-left"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-white/10 group-hover:bg-orange-500/20 flex items-center justify-center transition">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm">Bize Mesaj Gönderin</span>
                                    </button>
                                )}
                                {restaurant?.email && (
                                    <a href={`mailto:${restaurant.email}`} className="flex items-center space-x-3 text-gray-400 hover:text-orange-400 transition group">
                                        <div className="w-10 h-10 rounded-xl bg-white/10 group-hover:bg-orange-500/20 flex items-center justify-center transition">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm">{restaurant.email}</span>
                                    </a>
                                )}
                                {restaurant?.website && (
                                    <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-gray-400 hover:text-orange-400 transition group">
                                        <div className="w-10 h-10 rounded-xl bg-white/10 group-hover:bg-orange-500/20 flex items-center justify-center transition">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                            </svg>
                                        </div>
                                        <span className="text-sm">Web Sitesi</span>
                                    </a>
                                )}
                            </div>
                        </div>

                        {/* Social Media */}
                        <div>
                            <h3 className="text-xl font-bold mb-4 text-white">Sosyal Medya</h3>
                            <div className="flex flex-wrap gap-3">
                                {restaurant?.facebook_url && (
                                    <a 
                                        href={restaurant.facebook_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        aria-label="Facebook sayfamızı ziyaret edin"
                                        className="w-12 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-all hover:scale-110 shadow-lg shadow-blue-600/30"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                        </svg>
                                    </a>
                                )}
                                {restaurant?.instagram_url && (
                                    <a 
                                        href={restaurant.instagram_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        aria-label="Instagram sayfamızı ziyaret edin"
                                        className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:opacity-90 flex items-center justify-center text-white transition-all hover:scale-110 shadow-lg shadow-pink-500/30"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                        </svg>
                                    </a>
                                )}
                                {restaurant?.twitter_url && (
                                    <a 
                                        href={restaurant.twitter_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        aria-label="X (Twitter) sayfamızı ziyaret edin"
                                        className="w-12 h-12 rounded-xl bg-black hover:bg-gray-800 flex items-center justify-center text-white transition-all hover:scale-110 shadow-lg"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                        </svg>
                                    </a>
                                )}
                                {whatsappUrl && isSettingEnabled('enable_whatsapp', true) && (
                                    <a 
                                        href={whatsappUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        aria-label="WhatsApp ile iletişime geçin"
                                        className="w-12 h-12 rounded-xl bg-green-500 hover:bg-green-600 flex items-center justify-center text-white transition-all hover:scale-110 shadow-lg shadow-green-500/30"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                        </svg>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Copyright */}
                    <div className="mt-12 pt-8 border-t border-gray-800">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                            <p className="text-sm text-gray-500">
                                Zehuma QR Menü © {new Date().getFullYear()}
                            </p>
                            <a 
                                href="https://zehuma.com" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-gray-600 hover:text-orange-400 transition-colors"
                            >
                                Zehuma Bilgi Teknolojileri A.Ş.
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default MenuFooter;
